import { GROUP_SEPARATOR, numberCell, textCell } from './mapper.helpers';

import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';
import type { GroupNameResolver, ListExport } from '../export.types';

/** Brand and category of a POI, as returned by the sources enrichment endpoint. */
export type PoiInfo = { brand?: string; category?: string };

export type VenuesExportOptions = {
  /** Attribution POIs add the Brand and Category columns; proximity points do not. */
  includeBrandAndCategory: boolean;
  /** POI info by `geoDataId`. */
  poiInfo?: Record<string, PoiInfo>;
};

// The v1 venues export (batchExportVenues.service) without its address, city, state, zip code and phone columns
const HEADERS = ['Venue Name', 'Country', 'Latitude', 'Longitude', 'Group'];
const BRAND_AND_CATEGORY_HEADERS = ['Brand', 'Category'];

const brandAndCategory = (venue: Partial<Venue>, poiInfo: Record<string, PoiInfo>) => {
  const info = venue.geoDataId ? poiInfo[venue.geoDataId] : undefined;
  return [textCell(info?.brand), textCell(info?.category) || (venue.categories ?? []).join(GROUP_SEPARATOR)];
};

/** Table of the selected (non deleted) venues. */
export const buildVenuesExport = (
  venues: Partial<Venue>[],
  selectedIds: string[],
  groupNames: GroupNameResolver,
  { includeBrandAndCategory, poiInfo = {} }: VenuesExportOptions
): ListExport => {
  const selected = new Set(selectedIds);
  const exported = venues.filter(venue => !venue.deleted && selected.has(String(venue._id)));
  const rows = exported.map(venue => [
    textCell(venue.name),
    textCell(venue.country),
    numberCell(venue.latlng?.lat),
    numberCell(venue.latlng?.lng),
    groupNames(venue.groups).join(GROUP_SEPARATOR),
    ...(includeBrandAndCategory ? brandAndCategory(venue, poiInfo) : [])
  ]);
  return {
    excludedCount: 0,
    table: { headers: includeBrandAndCategory ? [...HEADERS, ...BRAND_AND_CATEGORY_HEADERS] : HEADERS, rows }
  };
};
